/**
 * BaseInventoryPanel - Componente genérico de inventario
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Panel de inventario configurable que implementa:
 * - Paginación dinámica (ResizeObserver)
 * - Sorting por columnas
 * - Multi-selección (checkboxes + acciones masivas)
 * - Toggle visibilidad individual (eye button)
 * - Zoom a entidad en mapa
 * - Hover bidireccional tabla ↔ mapa
 * - Modos narrow/wide responsive
 * - Color picker (opcional)
 *
 * @remarks
 * Diseñado para eliminar duplicación de código entre paneles de inventario.
 * Sigue el patrón de TreesPanel pero 100% configurable.
 *
 * @example
 * ```tsx
 * <BaseInventoryPanel config={routesInventoryConfig} />
 * ```
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Eye, EyeOff, Search, Trash2,
  AlertCircle, Loader2,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { useProject } from '../../../projects';
import { ConfirmDialog } from '../../../components';
import { useLeftPanel } from './LeftPanel';
import { TablePagination } from './TablePagination';
import { useRightPanel } from '../RightPanel/RightPanelContext';
import { useModuleVisibilityStore } from '../../../module-store/module-visibility-store';
import { useModuleStatsStore } from '../../../module-store/module-stats-store';
import type {
  InventoryPanelConfig,
  InventoryRow,
  InventoryColumnConfig,
} from './inventory-panel-types';
import './base-inventory-panel.css';

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

/** Altura aproximada de cada fila en px — sincronizar con CSS min-height. */
const ROW_HEIGHT_PX = 26;

/** Paleta de colores predefinidos para el picker */
const COLOR_PRESETS = [
  '#ffffff', '#cbd5e1', '#94a3b8', '#64748b',
  '#334155', '#0f172a', '#6366f1', '#3b82f6',
  '#06b6d4', '#14b8a6', '#22c55e', '#84cc16',
  '#f59e0b', '#f97316', '#ef4444', '#ec4899',
  '#8b5cf6', '#a855f7', '#78716c', '#0ea5e9',
  '#d946ef', '#f43f5e', '#10b981', '#eab308',
];

// ─────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────

/**
 * Obtiene el valor formateado de una celda.
 */
function getCellValue(row: InventoryRow, col: InventoryColumnConfig): string {
  const rawValue = row[col.key];
  if (rawValue === null || rawValue === undefined) return '—';
  if (col.format) return col.format(rawValue);
  return String(rawValue);
}

/**
 * Genera el grid-template-columns para CSS.
 * Incluye los controles fijos (checkbox, eye, zoom) + columnas de datos.
 */
function buildGridTemplate(columns: InventoryColumnConfig[]): string {
  const controls = '18px 20px 20px'; // checkbox + eye + zoom
  const dataCols = columns.map(c => c.width).join(' ');
  return `${controls} ${dataCols}`;
}

// ─────────────────────────────────────────
// ColorPalette Component
// ─────────────────────────────────────────

/**
 * Paleta de colores flotante (portal, fixed position).
 */
function ColorPalette({
  anchorRef,
  currentColor,
  onSelect,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLSpanElement | null>;
  currentColor: string;
  onSelect: (hex: string) => void;
  onClose: () => void;
}): JSX.Element | null {
  const paletteRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Position next to swatch
  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
  }, [anchorRef]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        paletteRef.current && !paletteRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef, onClose]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={paletteRef}
      className="bip-color-palette"
      style={{ top: pos.top, left: pos.left }}
    >
      {COLOR_PRESETS.map((hex) => (
        <span
          key={hex}
          className={`bip-color-preset${hex === currentColor ? ' active' : ''}`}
          style={{ backgroundColor: hex }}
          onClick={() => onSelect(hex)}
        />
      ))}
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────

/**
 * Panel de inventario genérico configurable.
 *
 * @template T Tipo de la entidad del módulo
 */
export function BaseInventoryPanel<T>({
  config,
}: {
  config: InventoryPanelConfig<T>;
}): JSX.Element {
  const { projectId } = useProject();
  const { collapsed } = useLeftPanel();
  const { setActivePanel, canChangeContent } = useRightPanel();
  const { isModuleHidden } = useModuleVisibilityStore();
  const { updateElementType } = useModuleStatsStore();

  // ── Data from hook ──
  const { data, isLoading, error: apiError, reload, delete: deleteItem } = config.useData(projectId || '');

  // ── Color picker (opcional) ──
  const [showPalette, setShowPalette] = useState(false);
  const swatchRef = useRef<HTMLSpanElement>(null);
  const elementColor = config.colorStore?.getColor(config.colorStore.colorKey) ?? '#ffffff';

  // ── Data state ──
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Build rows cuando data cambia
  useEffect(() => {
    setRows(data.map(config.buildRow));
  }, [data, config]);

  // Update stats
  useEffect(() => {
    if (config.moduleId) {
      updateElementType(
        config.moduleId,
        config.entityType,
        config.displayName,
        rows.length
      );
    }
  }, [config.moduleId, config.entityType, config.displayName, rows.length, updateElementType]);

  // ── Visibility (eye toggle per element) ──
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // ── Multi-select (checkbox) ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Sort ──
  const [sortKey, setSortKey] = useState<string>('code');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const bodyRef = useCallback((el: HTMLDivElement | null) => setBodyEl(el), []);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // ── Hover (bidirectional map ↔ table) ──
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // ── Event listeners ──
  useEffect(() => {
    const handleCreated = (): void => { void reload(); };
    const handleUpdated = (): void => { void reload(); };
    const handleDeleted = (e: Event): void => {
      const detail = (e as CustomEvent).detail;
      const deletedId = detail[`${config.entityType}Id`] || detail.id;
      if (deletedId) {
        setRows(prev => prev.filter(r => r.id !== deletedId));
      }
    };

    window.addEventListener(config.events.created, handleCreated);
    window.addEventListener(config.events.updated, handleUpdated);
    window.addEventListener(config.events.deleted, handleDeleted);

    return () => {
      window.removeEventListener(config.events.created, handleCreated);
      window.removeEventListener(config.events.updated, handleUpdated);
      window.removeEventListener(config.events.deleted, handleDeleted);
    };
  }, [config, reload]);

  // ── Module visibility sync ──
  useEffect(() => {
    if (!config.moduleId) return;

    const handleModuleVisibilityChanged = (e: Event): void => {
      const { moduleId, visible } = (e as CustomEvent<{ moduleId: string; visible: boolean }>).detail;
      if (moduleId !== config.moduleId) return;

      if (visible) {
        // Módulo visible: mostrar todos
        setHiddenIds(new Set());
        rows.forEach(row => {
          window.dispatchEvent(new CustomEvent(config.events.visibilityToggle, {
            detail: { [`${config.entityType}Id`]: row.id, visible: true },
          }));
        });
      } else {
        // Módulo oculto: ocultar todos
        setHiddenIds(new Set(rows.map(r => r.id)));
        rows.forEach(row => {
          window.dispatchEvent(new CustomEvent(config.events.visibilityToggle, {
            detail: { [`${config.entityType}Id`]: row.id, visible: false },
          }));
        });
      }
    };

    window.addEventListener('module-visibility-changed', handleModuleVisibilityChanged);
    return () => {
      window.removeEventListener('module-visibility-changed', handleModuleVisibilityChanged);
    };
  }, [rows, config]);

  // Sincronizar hiddenIds con estado del módulo al cargar
  useEffect(() => {
    if (config.moduleId && isModuleHidden(config.moduleId)) {
      const newHiddenIds = new Set(rows.map(r => r.id));
      setHiddenIds(newHiddenIds);
      rows.forEach(row => {
        window.dispatchEvent(new CustomEvent(config.events.visibilityToggle, {
          detail: { [`${config.entityType}Id`]: row.id, visible: false },
        }));
      });
    }
  }, [rows, config, isModuleHidden]);

  // ── ResizeObserver: rows per page ──
  useEffect(() => {
    if (!bodyEl) return;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setRowsPerPage(Math.max(1, Math.floor(h / ROW_HEIGHT_PX)));
    });
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  // ── Sort handler ──
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // ── Sorted rows ──
  const sortedRows = useMemo(() => {
    const col = [...config.narrowColumns, ...config.wideColumns].find(c => c.key === sortKey);
    if (!col) return rows;

    return [...rows].sort((a, b) => {
      const aVal = getCellValue(a, col);
      const bVal = getCellValue(b, col);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir, config]);

  // Ajustar página si rowsPerPage cambia
  useEffect(() => {
    const max = Math.ceil(sortedRows.length / rowsPerPage) || 1;
    if (page > max) setPage(max);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, sortedRows.length]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

  const pageRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // ── Visibility toggle (individual) ──
  const handleToggleVisibility = (id: string): void => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      const willHide = !next.has(id);
      if (willHide) { next.add(id); } else { next.delete(id); }
      window.dispatchEvent(new CustomEvent(config.events.visibilityToggle, {
        detail: { [`${config.entityType}Id`]: id, visible: !willHide },
      }));
      return next;
    });
  };

  // ── Bulk visibility toggle ──
  const handleBulkToggleVisibility = (): void => {
    const anyVisible = [...selectedIds].some(id => !hiddenIds.has(id));
    setHiddenIds(prev => {
      const next = new Set(prev);
      selectedIds.forEach(id => {
        if (anyVisible) { next.add(id); } else { next.delete(id); }
        window.dispatchEvent(new CustomEvent(config.events.visibilityToggle, {
          detail: { [`${config.entityType}Id`]: id, visible: !anyVisible },
        }));
      });
      return next;
    });
  };

  // ── Checkbox: individual ──
  const handleToggleSelect = (id: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // ── Checkbox: select/deselect all on page ──
  const handleSelectAll = (): void => {
    const allSelected = pageRows.every(r => selectedIds.has(r.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageRows.forEach(r => next.delete(r.id));
      } else {
        pageRows.forEach(r => next.add(r.id));
      }
      return next;
    });
  };

  // ── Zoom to entity in map ──
  const handleZoom = (row: InventoryRow): void => {
    const originalItem = data.find((item: any) => config.buildRow(item).id === row.id);
    if (!originalItem) return;

    const target = config.getZoomTarget(originalItem);
    window.dispatchEvent(new CustomEvent(config.events.zoom, {
      detail: {
        [`${config.entityType}Id`]: row.id,
        ...target,
      },
    }));
  };

  // ── Open details panel ──
  const handleCodeClick = async (id: string): Promise<void> => {
    if (!config.detailsPanelType) return;

    // Verificar cambios sin guardar
    const canChange = await canChangeContent();
    if (!canChange) return;

    // Dispatch evento de selección
    window.dispatchEvent(new CustomEvent(config.events.select, {
      detail: { [`${config.entityType}Id`]: id, openPanel: true },
    }));

    // Abrir panel derecho
    setActivePanel(config.detailsPanelType);
  };

  // ── Bulk delete ──
  const handleBulkDelete = async (): Promise<void> => {
    const count = selectedIds.size;
    if (count === 0) return;

    const confirmed = await ConfirmDialog.show({
      title: `¿Eliminar ${count} ${count === 1 ? config.displayNameSingular : config.displayName.toLowerCase()}?`,
      message: `Se eliminarán ${count} ${count === 1 ? config.displayNameSingular : config.displayName.toLowerCase()} permanentemente.\n\nEsta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
    });
    if (!confirmed) return;

    const toDelete = [...selectedIds];
    const results = await Promise.allSettled(toDelete.map(id => deleteItem(id)));

    const deleted = toDelete.filter((_, i) => results[i].status === 'fulfilled' && (results[i] as PromiseFulfilledResult<boolean>).value);
    const failed  = toDelete.filter((_, i) => results[i].status === 'rejected' || !(results[i] as PromiseFulfilledResult<boolean>).value);

    if (deleted.length > 0) {
      const deletedSet = new Set(deleted);
      setRows(prev => prev.filter(r => !deletedSet.has(r.id)));
      setSelectedIds(prev => { const next = new Set(prev); deleted.forEach(id => next.delete(id)); return next; });
      deleted.forEach(id =>
        window.dispatchEvent(new CustomEvent(config.events.deleted, {
          detail: { [`${config.entityType}Id`]: id },
        }))
      );
    }
    if (failed.length > 0) {
      console.error(`[BaseInventoryPanel] No se pudieron eliminar:`, failed);
      setError(`No se ${failed.length === 1 ? `pudo eliminar 1 ${config.displayNameSingular}` : `pudieron eliminar ${failed.length} ${config.displayName.toLowerCase()}`}. Verificá la conexión.`);
    }
  };

  // ── Sort icon helper ──
  const sortIcon = (key: string): React.ReactNode => {
    if (sortKey !== key) return null;
    return sortDir === 'asc'
      ? <ChevronUp   size={9} className="bip-sort-icon" />
      : <ChevronDown size={9} className="bip-sort-icon" />;
  };

  // ── Derived state ──
  const hasSelection     = selectedIds.size > 0;
  const allOnPageSelected = pageRows.length > 0 && pageRows.every(r => selectedIds.has(r.id));
  const someOnPageSelected = pageRows.some(r => selectedIds.has(r.id));
  const anySelectedVisible = hasSelection && [...selectedIds].some(id => !hiddenIds.has(id));

  // ── Early return: no project ──
  if (!projectId) {
    return (
      <div className="bip-state">
        <AlertCircle size={20} />
        <p>No hay proyecto activo</p>
      </div>
    );
  }

  // ── Grid templates ──
  const narrowGrid = buildGridTemplate(config.narrowColumns);
  const wideGrid = buildGridTemplate(config.wideColumns);

  // ── Render ──
  return (
    <section className="bip-section" aria-label={`Inventario de ${config.displayName}`}>

      {/* ── Section header ── */}
      <header className={`bip-section-header${hasSelection ? ' has-selection' : ''}`}>
        {hasSelection ? (
          <>
            <span className="bip-selected-count">{selectedIds.size} seleccionados</span>
            <div className="bip-bulk-actions">
              <button
                className={`bip-bulk-btn${anySelectedVisible ? '' : ' all-hidden'}`}
                onClick={handleBulkToggleVisibility}
                title={anySelectedVisible ? 'Ocultar en mapa' : 'Mostrar en mapa'}
                aria-label={anySelectedVisible ? 'Ocultar seleccionados en mapa' : 'Mostrar seleccionados en mapa'}
              >
                {anySelectedVisible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button
                className="bip-bulk-btn bip-bulk-btn--danger"
                onClick={() => void handleBulkDelete()}
                title="Eliminar seleccionados"
                aria-label={`Eliminar ${config.displayName.toLowerCase()} seleccionados`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="bip-section-title">{config.displayName}</span>
            <span className="bip-section-count">{rows.length.toLocaleString()}</span>
            {config.colorStore && (
              <span className="bip-color-swatch-wrapper">
                <span
                  ref={swatchRef}
                  className="bip-color-swatch"
                  style={{ backgroundColor: elementColor }}
                  onClick={() => setShowPalette(p => !p)}
                  title="Cambiar color en mapa"
                />
                {showPalette && <ColorPalette
                  anchorRef={swatchRef}
                  currentColor={elementColor}
                  onSelect={(hex) => {
                    config.colorStore!.setColor(config.colorStore!.colorKey, hex);
                    setShowPalette(false);
                  }}
                  onClose={() => setShowPalette(false)}
                />}
              </span>
            )}
          </>
        )}
      </header>

      {/* ── Loading ── */}
      {isLoading && (
        <div className="bip-state">
          <Loader2 size={16} className="bip-spinner" />
          <p>Cargando…</p>
        </div>
      )}

      {/* ── Error ── */}
      {(error || apiError) && !isLoading && (
        <div className="bip-state bip-state--error">
          <AlertCircle size={16} />
          <p>{error || apiError}</p>
          <button className="bip-retry-btn" onClick={() => void reload()}>Reintentar</button>
        </div>
      )}

      {/* ── Empty ── */}
      {!isLoading && !error && !apiError && rows.length === 0 && (
        <div className="bip-state bip-state--empty">
          <p>No hay {config.displayName.toLowerCase()} registrados</p>
        </div>
      )}

      {/* ── Table ── */}
      {!isLoading && !error && !apiError && rows.length > 0 && (
        <>
          {/* ════ Narrow mode ════ */}
          {!collapsed && (
            <>
              <div className="bip-header bip-header--narrow" style={{ gridTemplateColumns: narrowGrid }}>
                <input
                  type="checkbox"
                  className="bip-checkbox"
                  checked={allOnPageSelected}
                  ref={el => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={handleSelectAll}
                  title="Seleccionar todos en la página"
                  aria-label="Seleccionar todos"
                />
                <span className="bip-eye-placeholder" />
                <span className="bip-zoom-placeholder" />
                {config.narrowColumns.map(col => (
                  <button
                    key={col.key}
                    className={`bip-col-header${sortKey === col.key ? ' active' : ''}${col.key === 'name' ? ' bip-col-header--flex' : ''}`}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    title={col.title}
                  >
                    {col.label} {col.sortable !== false && sortIcon(col.key)}
                  </button>
                ))}
              </div>

              <div className="bip-body" ref={bodyRef}>
                {pageRows.map(row => {
                  const isHidden   = hiddenIds.has(row.id);
                  const isSelected = selectedIds.has(row.id);
                  const isHovered  = hoveredId === row.id;
                  return (
                    <div
                      key={row.id}
                      className={[
                        'bip-row bip-row--narrow',
                        isHidden   ? 'hidden'   : '',
                        isHovered  ? 'hovered'  : '',
                        isSelected ? 'selected' : '',
                      ].filter(Boolean).join(' ')}
                      style={{ gridTemplateColumns: narrowGrid }}
                      onMouseEnter={() => {
                        setHoveredId(row.id);
                        window.dispatchEvent(new CustomEvent(config.events.hover, {
                          detail: { [`${config.entityType}Id`]: row.id },
                        }));
                      }}
                      onMouseLeave={() => {
                        setHoveredId(null);
                        window.dispatchEvent(new CustomEvent(config.events.hover, {
                          detail: { [`${config.entityType}Id`]: null },
                        }));
                      }}
                    >
                      <input
                        type="checkbox"
                        className="bip-checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(row.id)}
                        onClick={e => e.stopPropagation()}
                        aria-label={`Seleccionar ${config.displayNameSingular} ${row.code ?? row.id}`}
                      />
                      <button
                        className={`bip-eye-btn${isHidden ? ' hidden' : ''}`}
                        onClick={e => { e.stopPropagation(); handleToggleVisibility(row.id); }}
                        title={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                        aria-label={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                      >
                        {isHidden ? <EyeOff size={9} /> : <Eye size={9} />}
                      </button>
                      <button
                        className="bip-zoom-btn"
                        onClick={e => { e.stopPropagation(); handleZoom(row); }}
                        title="Acercarse en el mapa"
                        aria-label="Acercarse"
                      >
                        <Search size={9} />
                      </button>
                      {config.narrowColumns.map(col => {
                        const value = getCellValue(row, col);
                        if (col.key === 'code' && config.detailsPanelType) {
                          return (
                            <button
                              key={col.key}
                              className="bip-cell bip-cell--code bip-cell--clickable"
                              onClick={e => { e.stopPropagation(); void handleCodeClick(row.id); }}
                              title={`${value} - Clic para abrir propiedades`}
                              aria-label={`Abrir propiedades del ${config.displayNameSingular} ${value}`}
                            >
                              {value}
                            </button>
                          );
                        }
                        return (
                          <span
                            key={col.key}
                            className={`bip-cell bip-cell--${col.key}`}
                            title={value}
                          >
                            {value}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ════ Wide mode ════ */}
          {collapsed && (
            <>
              <div className="bip-header bip-header--wide" style={{ gridTemplateColumns: wideGrid }}>
                <input
                  type="checkbox"
                  className="bip-checkbox"
                  checked={allOnPageSelected}
                  ref={el => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={handleSelectAll}
                  title="Seleccionar todos en la página"
                  aria-label="Seleccionar todos"
                />
                <span className="bip-eye-placeholder" />
                <span className="bip-zoom-placeholder" />
                {config.wideColumns.map(col => (
                  <button
                    key={col.key}
                    className={`bip-col-header${sortKey === col.key ? ' active' : ''}`}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    title={`${col.title} — clic para ordenar`}
                  >
                    {col.label} {col.sortable !== false && sortIcon(col.key)}
                  </button>
                ))}
              </div>

              <div className="bip-body" ref={bodyRef}>
                {pageRows.map(row => {
                  const isHidden   = hiddenIds.has(row.id);
                  const isSelected = selectedIds.has(row.id);
                  const isHovered  = hoveredId === row.id;
                  return (
                    <div
                      key={row.id}
                      className={[
                        'bip-row bip-row--wide',
                        isHidden   ? 'hidden'   : '',
                        isHovered  ? 'hovered'  : '',
                        isSelected ? 'selected' : '',
                      ].filter(Boolean).join(' ')}
                      style={{ gridTemplateColumns: wideGrid }}
                      onMouseEnter={() => {
                        setHoveredId(row.id);
                        window.dispatchEvent(new CustomEvent(config.events.hover, {
                          detail: { [`${config.entityType}Id`]: row.id },
                        }));
                      }}
                      onMouseLeave={() => {
                        setHoveredId(null);
                        window.dispatchEvent(new CustomEvent(config.events.hover, {
                          detail: { [`${config.entityType}Id`]: null },
                        }));
                      }}
                    >
                      <input
                        type="checkbox"
                        className="bip-checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(row.id)}
                        onClick={e => e.stopPropagation()}
                        aria-label={`Seleccionar ${config.displayNameSingular} ${row.code ?? row.id}`}
                      />
                      <button
                        className={`bip-eye-btn${isHidden ? ' hidden' : ''}`}
                        onClick={e => { e.stopPropagation(); handleToggleVisibility(row.id); }}
                        title={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                      >
                        {isHidden ? <EyeOff size={9} /> : <Eye size={9} />}
                      </button>
                      <button
                        className="bip-zoom-btn"
                        onClick={e => { e.stopPropagation(); handleZoom(row); }}
                        title="Acercarse en el mapa"
                      >
                        <Search size={9} />
                      </button>
                      {config.wideColumns.map(col => {
                        const value = getCellValue(row, col);
                        if (col.key === 'code' && config.detailsPanelType) {
                          return (
                            <button
                              key={col.key}
                              className="bip-cell bip-cell--code bip-cell--clickable"
                              onClick={e => { e.stopPropagation(); void handleCodeClick(row.id); }}
                              title={`${value} - Clic para abrir propiedades`}
                              aria-label={`Abrir propiedades del ${config.displayNameSingular} ${value}`}
                            >
                              {value}
                            </button>
                          );
                        }
                        return (
                          <span
                            key={col.key}
                            className={`bip-cell bip-cell--${col.key}`}
                            title={value}
                          >
                            {value}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </section>
  );
}
